import {_} from "i18n";
import "./TeamStatsItem.css";
import clsx from "clsx";
import {TeamStatCalculationResult, TeamStatType} from "./definition";
import { GamePageLazyResponse, GamePageResponse, GPPermissionsResponse } from "../../types";
import {calculateTeamStats} from "./functions";
import {Keys} from "i18n/locales/types";
import { useGameStore } from "../../store";
import type { TeamTaggedSide } from "../../utils/teamTagged";

export interface GameDataTeamItemProps {
	statType: TeamStatType;
	periods: string[];
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	handleShowModal: (result: TeamStatCalculationResult) => void;
	taggedSide?: TeamTaggedSide | null;
}

enum TeamStatsBarPosition {
	LEFT = "left",
	RIGHT = "right",
}

interface TeamStatsBarProps {
	position: "left" | "right";
	width: number;
}

const TeamStatsBar = ({ position, width }: TeamStatsBarProps) => {
	return (
		<div className={`team-stat-progress team-stat-progress--${position}`}>
			<div className="team-stat-progress__value" style={{ width: `${width}%` }} />
		</div>
	);
};

const isItemClickable = (
	permissions: GPPermissionsResponse,
	calculationResult: TeamStatCalculationResult
) => {
	if (permissions.showEvents && calculationResult.isFilterable) {
		return true;
	}

	if (calculationResult.isDescriptionOnly) {
		return true;
	}

	return false;
}

const emptyCalculation = (statType: TeamStatType, gameData: GamePageResponse): TeamStatCalculationResult => ({
	isGoal: false,
	label: `gamePage.teamStats.type.${statType}`,
	description: `gamePage.teamStats.description.${statType}`,
	homeGameTeamId: gameData.homeGameTeam.id,
	awayGameTeamId: gameData.awayGameTeam.id,
	homeEvents: [],
	awayEvents: [],
	allEvents: [],
	homeValue: 0,
	homeValueFormatted: "0",
	awayValue: 0,
	awayValueFormatted: "0",
	allValue: 0,
	filterValues: [],
	isFilterable: false,
	isDescriptionOnly: false,
});

export function TeamStatsItem({
	statType,
	periods,
	gameData,
	gameDataLazy,
	handleShowModal,
	taggedSide = null,
}: GameDataTeamItemProps) {
	const { eventFilter } = useGameStore();
	const calculation = eventFilter
		? calculateTeamStats(eventFilter, gameData, statType, periods, gameDataLazy)
		: emptyCalculation(statType, gameData);
	const isClickable = isItemClickable(gameData.permissions, calculation);
	const isOneTeam = taggedSide !== null;

	let leftWidth = 0;
	let rightWidth = 0;

	if (calculation.allValue > 0) {
		leftWidth = (calculation.homeValue / calculation.allValue) * 100;
		rightWidth = (calculation.awayValue / calculation.allValue) * 100;
	}

	const taggedValueFormatted =
		taggedSide === "away" ? calculation.awayValueFormatted : calculation.homeValueFormatted;

	return (
		<div
			className={clsx("team-stat", {
				"team-stat--clickable": isClickable,
				"team-stat--one-team": isOneTeam,
			})}
			onClick={isClickable ? () => handleShowModal(calculation) : undefined}
		>
			{isOneTeam === false && (
				<div className="team-stat__value team-stat__value--left">
					{calculation.homeValueFormatted}
				</div>
			)}

			<div className="team-stat__title-wrapper">
				<div className="team-stat__title">
					{_(calculation.label as keyof Keys)}
				</div>
			</div>

			<div className={clsx("team-stat__value", isOneTeam ? "team-stat__value--one-team" : "team-stat__value--right")}>
				{isOneTeam ? taggedValueFormatted : calculation.awayValueFormatted}
			</div>
			{isOneTeam === false && (
				<div className="team-stat__bar">
					<TeamStatsBar position={TeamStatsBarPosition.LEFT} width={leftWidth} />
					<TeamStatsBar position={TeamStatsBarPosition.RIGHT} width={rightWidth} />
				</div>
			)}
		</div>
	);
}

export default TeamStatsItem;
